"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import VoiceAgentApi from "@/Apis/VoiceAgentApi";
import { romanDraft } from "./romanDraft";
import { RealtimeVoiceSession } from "./realtime";

// Self-contained theme for this component only (not the app's MUI palette),
// so it can be dropped into any project unchanged.
const BRAND = {
  main: "#C62828",
  dark: "#8E1414",
  soft: "#FBEAEA",
  softer: "#FDF6F5",
  border: "#F3DBD9",
};

const textFieldBrandSx = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: BRAND.main,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: BRAND.main,
  },
};

// Storefront base domain — product page = base + "/" + handle.
// Derived from the current browser host by dropping a leading admin/dashboard/cms
// subdomain (e.g. admin.alfamall.com -> alfamall.com). Works in both CRA and Next.js.
// SSR-safe: returns "" on the server (product links resolve on the client, on click).
const getStorefrontBase = () => {
  if (typeof window === "undefined") return "";

  const { protocol, hostname, host } = window.location;
  const storefrontHost = hostname.replace(/^(admin|dashboard|cms|backoffice|panel)[.-]/i, "");
  const port = host.includes(":") ? `:${host.split(":")[1]}` : "";
  return `${protocol}//${storefrontHost}${port}`.replace(/\/+$/, "");
};

const openProduct = (product) => {
  if (!product?.handle) return;
  const url = `${getStorefrontBase()}/${encodeURIComponent(product.handle)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

const formatPrice = (value) =>
  value || value === 0
    ? `PKR ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : "Price on request";

const BENEFITS = [
  { icon: <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />, label: "Order status" },
  { icon: <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />, label: "Delivery help" },
  { icon: <ReplayOutlinedIcon sx={{ fontSize: 16 }} />, label: "Refunds & returns" },
  { icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />, label: "English & Roman Urdu" },
];

const ENGINE_LABEL = {
  v2: "Connected Agent",
  openai: "OpenAI working",
};

const VoiceAgentPanel = ({ onClose, engine = "openai" }) => {
  const [customer, setCustomer] = useState({ customerName: "", customerEmail: "" });
  const [status, setStatus] = useState("Ready to connect");
  const [error, setError] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(false);
  const [messages, setMessages] = useState([]);
  const sessionRef = useRef(null);
  const transcriptRef = useRef(null);

  const formReady = customer.customerName.trim() && customer.customerEmail.trim();

  useEffect(() => {
    transcriptRef.current?.scrollTo({
      top: transcriptRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  useEffect(() => {
    if (engine !== "v2") return undefined;
    // Compile the ElevenLabs client while the form is on screen, before Start is clicked.
    import("./elevenlabs").catch(() => {});
    return undefined;
  }, [engine]);

  useEffect(() => {
    return () => {
      sessionRef.current?.end("abandoned");
    };
  }, []);

  const handleMessage = (message) => {
    setMessages((previous) => {
      const withoutDraft = previous.filter((m) => !m.draft);
      return [...withoutDraft, message];
    });
  };

  const connect = async () => {
    setError("");
    setMessages([]);
    setConnecting(true);
    try {
      let Session = RealtimeVoiceSession;
      if (engine === "v2") {
        try {
          Session = (await import("./elevenlabs")).ElevenLabsVoiceSession;
        } catch {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          Session = (await import("./elevenlabs")).ElevenLabsVoiceSession;
        }
      }
      const session = new Session({
        customer,
        onStatus: setStatus,
        onMessage: handleMessage,
        onError: setError,
        onDisconnect: () => {
          setConnected(false);
          setConnecting(false);
          setMuted(false);
        },
      });
      sessionRef.current = session;
      await session.connect();
      setConnected(true);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Connection failed");
      setStatus("Connection failed");
    } finally {
      setConnecting(false);
    }
  };

  const endChat = async (statusValue = "ended") => {
    await sessionRef.current?.end(statusValue);
    sessionRef.current = null;
    setConnected(false);
    setMuted(false);
    setStatus("Ready to connect");
  };

  const handleClose = async () => {
    if (connected || connecting) {
      await endChat("abandoned");
    }
    onClose?.();
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    sessionRef.current?.setMicMuted(next);
    setStatus(next ? "Microphone muted" : "Connected");
  };

  const humanHandoff = async () => {
    try {
      await VoiceAgentApi.createHandoff({
        ...customer,
        reason: "Customer requested human agent from storefront",
      });
      await endChat("handoff");
      setStatus("Human agent requested");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: BRAND.softer,
        overflow: "hidden",
      }}
    >
      {/* Drawer top bar */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, md: 3 },
          py: 1.5,
          bgcolor: "#fff",
          borderBottom: `1px solid ${BRAND.border}`,
          zIndex: 2,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              background: `linear-gradient(135deg, ${BRAND.main}, ${BRAND.dark})`,
            }}
          >
            <HeadsetMicIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                AI Voice Support
              </Typography>
              <Chip
                size="small"
                label={ENGINE_LABEL[engine] || ENGINE_LABEL.openai}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  bgcolor: engine === "v2" ? "#E8F5E9" : "#E3F2FD",
                  color: engine === "v2" ? "#1B5E20" : "#0D47A1",
                }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              AlfaMall AI CX Agent
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={handleClose} aria-label="Close voice support" sx={{ color: BRAND.main }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable content — same design as admin */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", p: { xs: 2, md: 3 } }}>
        {/* Hero */}
        <Paper
          elevation={0}
          sx={{
            maxWidth: 1200,
            mx: "auto",
            mb: 3,
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            background: `linear-gradient(120deg, #ffffff 55%, ${BRAND.soft})`,
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
          >
            <Box sx={{ maxWidth: 620 }}>
              <Typography
                variant="overline"
                sx={{ color: BRAND.main, fontWeight: 700, letterSpacing: 1.5 }}
              >
                Customer Care
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 1 }}>
                Support that listens, understands, and helps.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Speak with AI support for orders, delivery, refunds, returns, payments,
                complaints, and product questions.
              </Typography>
            </Box>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                minWidth: 260,
                borderRadius: 3,
                color: "#fff",
                background: `linear-gradient(135deg, ${BRAND.main}, ${BRAND.dark})`,
              }}
            >
              <AutoAwesomeIcon sx={{ mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                English & Roman Urdu
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Professional voice assistance with human handoff when needed.
              </Typography>
            </Paper>
          </Stack>
        </Paper>

        {/* Main two-column layout */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          sx={{ maxWidth: 1200, mx: "auto" }}
          alignItems="stretch"
        >
          {/* Support card */}
          <Paper
            elevation={0}
            sx={{
              flex: { xs: "1 1 auto", md: "1 1 0" },
              minWidth: 0,
              width: { xs: "100%", md: "50%" },
              p: { xs: 2.5, md: 3 },
              borderRadius: 4,
              border: `1px solid ${BRAND.border}`,
              borderTop: `5px solid ${BRAND.main}`,
              bgcolor: "#fff",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                mb: 2.5,
                borderRadius: 3,
                bgcolor: BRAND.softer,
                border: `1px solid ${BRAND.border}`,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  background: `linear-gradient(135deg, ${BRAND.main}, ${BRAND.dark})`,
                }}
              >
                <HeadsetMicIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  AlfaMall AI CX Agent
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Professional customer support assistant
                </Typography>
              </Box>
            </Paper>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: BRAND.main, fontWeight: 700, letterSpacing: 1.5 }}
                >
                  Voice Support
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Connect with Support
                </Typography>
              </Box>
              <Chip
                size="small"
                label={connected ? "Live" : "Offline"}
                sx={{
                  fontWeight: 700,
                  bgcolor: connected ? "#E7F6EC" : "#F1F3F7",
                  color: connected ? "#1B7C3D" : "text.secondary",
                  "& .MuiChip-label": { px: 1.5 },
                }}
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      ml: 1,
                      borderRadius: "50%",
                      bgcolor: connected ? "#28965A" : "#9AA3B2",
                    }}
                  />
                }
              />
            </Stack>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
              {BENEFITS.map((benefit) => (
                <Chip
                  key={benefit.label}
                  icon={benefit.icon}
                  label={benefit.label}
                  size="small"
                  sx={{
                    bgcolor: "#fff",
                    border: `1px solid ${BRAND.border}`,
                    fontWeight: 600,
                    color: "text.primary",
                    "& .MuiChip-icon": { color: BRAND.main },
                  }}
                />
              ))}
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2.5 }}>
              <TextField
                label="Full name"
                placeholder="Enter your name"
                size="small"
                fullWidth
                value={customer.customerName}
                onChange={(e) => setCustomer({ ...customer, customerName: e.target.value })}
                disabled={connected || connecting}
                sx={textFieldBrandSx}
              />
              <TextField
                label="Email address"
                placeholder="name@example.com"
                size="small"
                fullWidth
                value={customer.customerEmail}
                onChange={(e) => setCustomer({ ...customer, customerEmail: e.target.value })}
                disabled={connected || connecting}
                sx={textFieldBrandSx}
              />
            </Stack>

            <Paper
              elevation={0}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.25,
                pl: 2,
                mb: 2,
                borderRadius: 3,
                bgcolor: BRAND.softer,
                border: `1px solid ${BRAND.border}`,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: connected ? "#28965A" : "#9AA3B2",
                    boxShadow: connected ? "0 0 0 4px rgba(40,150,90,0.18)" : "none",
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {connecting ? "Connecting..." : status}
                </Typography>
              </Stack>
              {connected && (
                <Button
                  size="small"
                  variant={muted ? "contained" : "outlined"}
                  startIcon={muted ? <MicOffIcon /> : <MicIcon />}
                  onClick={toggleMute}
                  sx={{
                    borderRadius: 5,
                    textTransform: "none",
                    fontWeight: 700,
                    ...(muted
                      ? { bgcolor: BRAND.main, "&:hover": { bgcolor: BRAND.dark } }
                      : {
                          color: BRAND.main,
                          borderColor: BRAND.main,
                          "&:hover": { borderColor: BRAND.dark, bgcolor: BRAND.soft },
                        }),
                  }}
                >
                  {muted ? "Unmute" : "Mute"}
                </Button>
              )}
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
              <Button
                variant="contained"
                size="large"
                disabled={!formReady || connected || connecting}
                onClick={connect}
                startIcon={
                  connecting ? <CircularProgress size={18} color="inherit" /> : <MicIcon />
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 3,
                  color: "#fff",
                  background: `linear-gradient(135deg, ${BRAND.main}, ${BRAND.dark})`,
                  "&.Mui-disabled": {
                    background: "#E3C8C6",
                    color: "#fff",
                  },
                }}
              >
                {connecting ? "Connecting..." : "Start Voice Support"}
              </Button>
              <Button
                variant="outlined"
                size="large"
                disabled={!connected}
                onClick={() => endChat("ended")}
                startIcon={<PhoneDisabledIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 3,
                  color: BRAND.main,
                  borderColor: BRAND.main,
                  "&:hover": { borderColor: BRAND.dark, bgcolor: BRAND.soft },
                }}
              >
                End Chat
              </Button>
              <Button
                variant="outlined"
                size="large"
                disabled={!formReady}
                onClick={humanHandoff}
                startIcon={<PersonSearchIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 3,
                  color: BRAND.main,
                  borderColor: BRAND.main,
                  "&:hover": { borderColor: BRAND.dark, bgcolor: BRAND.soft },
                }}
              >
                Human Agent
              </Button>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: 3,
                bgcolor: BRAND.softer,
                border: `1px solid ${BRAND.border}`,
              }}
            >
              <MailOutlineIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                Your conversation may be saved to help resolve tickets and improve support
                quality.
              </Typography>
            </Paper>
          </Paper>

          {/* Transcript card */}
          <Paper
            elevation={0}
            sx={{
              flex: { xs: "1 1 auto", md: "1 1 0" },
              minWidth: 0,
              width: { xs: "100%", md: "50%" },
              maxWidth: { xs: "100%", md: "50%" },
              overflow: "hidden",
              p: { xs: 2.5, md: 3 },
              borderRadius: 4,
              border: `1px solid ${BRAND.border}`,
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
              minHeight: { xs: 420, md: 560 },
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: BRAND.main, fontWeight: 700, letterSpacing: 1.5 }}
            >
              Conversation
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
              Live Transcript
            </Typography>

            <Box
              ref={transcriptRef}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                minHeight: 320,
                maxHeight: { xs: 420, md: 560 },
                overflowY: "auto",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                pr: 0.5,
              }}
            >
              {messages.length === 0 && (
                <Box
                  sx={{
                    m: "auto",
                    textAlign: "center",
                    color: "text.secondary",
                    maxWidth: 300,
                  }}
                >
                  <MicIcon sx={{ fontSize: 32, color: BRAND.main, mb: 1 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "text.primary" }}
                  >
                    Transcript will appear here
                  </Typography>
                  <Typography variant="body2">
                    Allow microphone access and start speaking with the AI support agent.
                  </Typography>
                </Box>
              )}
              {messages.map((message, index) => {
                if (message.type === "products") {
                  return (
                    <Box key={`products-${index}`} sx={{ alignSelf: "stretch" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: BRAND.main,
                          display: "block",
                          mb: 0.75,
                        }}
                      >
                        Products for &quot;{romanDraft(message.query)}&quot;
                      </Typography>
                      <Stack spacing={1}>
                        {message.products.map((product) => (
                          <Paper
                            key={product.id || product.title}
                            elevation={0}
                            onClick={() => openProduct(product)}
                            sx={{
                              p: 1.25,
                              borderRadius: 3,
                              border: `1px solid ${BRAND.border}`,
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              "&:hover": { bgcolor: BRAND.softer, borderColor: BRAND.main },
                            }}
                          >
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 2,
                                flexShrink: 0,
                                overflow: "hidden",
                                bgcolor: BRAND.soft,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {product.image ? (
                                <Box
                                  component="img"
                                  src={product.image}
                                  alt={product.title}
                                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <ImageNotSupportedOutlinedIcon
                                  sx={{ color: BRAND.main, fontSize: 20 }}
                                />
                              )}
                            </Box>
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                                {product.title}
                              </Typography>
                              {product.brand && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {product.brand}
                                </Typography>
                              )}
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mt: 0.5 }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 800, color: BRAND.main }}
                                >
                                  {formatPrice(product.price)}
                                </Typography>
                                {product.compareAtPrice > product.price && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ textDecoration: "line-through" }}
                                  >
                                    {formatPrice(product.compareAtPrice)}
                                  </Typography>
                                )}
                                <Chip
                                  size="small"
                                  label={product.inStock ? "In stock" : "Out of stock"}
                                  sx={{
                                    height: 20,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    ...(product.inStock
                                      ? { bgcolor: "#E7F6EC", color: "#1B7C3D" }
                                      : { bgcolor: "#F1F3F7", color: "#5B6472" }),
                                  }}
                                />
                              </Stack>
                            </Box>
                            <OpenInNewIcon sx={{ color: BRAND.main, fontSize: 18 }} />
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  );
                }

                return (
                  <Paper
                    key={`${message.role}-${index}`}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      maxWidth: "85%",
                      minWidth: 0,
                      alignSelf: message.role === "customer" ? "flex-end" : "flex-start",
                      bgcolor: message.role === "customer" ? BRAND.soft : "#fff",
                      border: `1px solid ${BRAND.border}`,
                      opacity: message.draft ? 0.7 : 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: BRAND.main, display: "block", mb: 0.25 }}
                    >
                      {message.role === "customer" ? "You" : "AlfaMall"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                    >
                      {romanDraft(message.text)}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
};

export default VoiceAgentPanel;
